import {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import useLiterals from 'utils/hooks/useLiterals';
import { getMercadoPagoInstance, paymentTypeIdToCardType } from 'utils/mercadopago';
import { mountField, cleanupEditCardFields } from 'utils/mercadopagoFields';
import Input from 'components/UI/Input';
import Button from 'components/UI/Button';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import Debugger from 'utils/debugger';
import { captureMercadoPagoError } from 'utils/sendSentryLog';

/**
 * Formulario para editar una tarjeta guardada usando Core Methods de Mercado Pago.
 * Usa literals.mercadopago.editCardForm para textos.
 */
const CardEditForm = ({
  card = null,
  onUpdate = () => {},
  onCancel = () => {},
  onDelete = () => {},
}) => {
  const literals = useLiterals('mercadopago');
  const editFormLiterals = literals.editCardForm || {};
  const placeholders = editFormLiterals.placeholders || {};
  const [cardholderName, setCardholderName] = useState(card?.cardholder?.name || '');
  const [issuer, setIssuer] = useState('');
  const [issuerThumbnail, setIssuerThumbnail] = useState('');
  const [issuerName, setIssuerName] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState(card?.payment_method?.id || '');
  const [paymentTypeId, setPaymentTypeId] = useState(card?.payment_type_id || card?.payment_method?.payment_type_id || 'credit_card');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cardNumberFieldRef = useRef(null);
  const expirationDateFieldRef = useRef(null);
  const securityCodeFieldRef = useRef(null);
  const mpInstanceRef = useRef(null);
  const currentBinRef = useRef(null);

  const updateInstallments = useCallback(async (paymentMethod, bin) => {
    try {
      if (!mpInstanceRef.current) return;
      // Para edición no necesitamos amount, usamos un valor por defecto para obtener issuer
      const installmentsData = await mpInstanceRef.current.getInstallments({
        amount: '100',
        bin,
        paymentTypeId: 'credit_card',
      });

      if (installmentsData && installmentsData[0]) {
        const installmentData = installmentsData[0];

        setPaymentTypeId(installmentData.payment_type_id || 'credit_card');

        if (installmentData.issuer) {
          const issuerId = installmentData.issuer.id;
          const issuerThumb = installmentData.issuer.thumbnail || installmentData.issuer.secure_thumbnail;
          const issuerNameValue = installmentData.issuer.name;

          setIssuer(issuerId);
          setIssuerThumbnail(issuerThumb);
          setIssuerName(issuerNameValue);
        }
      }
    } catch (err) {
      Debugger.error('error getting installments:', err);
    }
  }, []);

  // Inicializar campos de Mercado Pago
  useEffect(() => {
    if (!card) return;

    const initFields = async () => {
      try {
        const mp = await getMercadoPagoInstance();
        mpInstanceRef.current = mp;

        // Esperar a que el DOM esté listo
        setTimeout(() => {
          (async () => {
            try {
              // Montar campos con IDs únicos para evitar conflictos
              const cardField = await mountField('cardNumber', 'edit-card-form__cardNumber', {
                placeholder: placeholders.cardNumber || 'Número de la tarjeta',
              });
              if (cardField && cardField.field && !cardNumberFieldRef.current) {
                cardNumberFieldRef.current = cardField.field;

                // Detectar método de pago cuando cambia el BIN
                cardNumberFieldRef.current.on('binChange', async (data) => {
                  const { bin } = data;

                  setTimeout(async () => {
                    try {
                      if (!bin && paymentMethodId) {
                        setPaymentMethodId('');
                        setPaymentTypeId('credit_card');
                        setIssuer('');
                        setIssuerThumbnail('');
                        setIssuerName('');
                        currentBinRef.current = null;
                        return;
                      }

                      if (bin && bin.length >= 6 && bin !== currentBinRef.current) {
                        currentBinRef.current = bin;

                        try {
                          const { results } = await mp.getPaymentMethods({ bin });

                          if (results && results.length > 0) {
                            const paymentMethod = results[0];
                            const newPaymentMethodId = paymentMethod.id;

                            setPaymentMethodId(newPaymentMethodId);
                            updateInstallments(paymentMethod, bin).catch((err) => {
                              Debugger.error('error updating installments:', err);
                            });
                          }
                        } catch (methodError) {
                          Debugger.error('error getting payment methods:', methodError);
                        }
                      }
                    } catch (err) {
                      Debugger.error('error in binChange handler:', err);
                    }
                  }, 50);
                });
              }

              const expField = await mountField('expirationDate', 'edit-card-form__expirationDate', {
                placeholder: placeholders.expiration || 'MM/YY',
              });
              if (expField && expField.field && !expirationDateFieldRef.current) {
                expirationDateFieldRef.current = expField.field;
              }

              const secField = await mountField('securityCode', 'edit-card-form__securityCode', {
                placeholder: placeholders.securityCode || 'Código de seguridad',
              });
              if (secField && secField.field && !securityCodeFieldRef.current) {
                securityCodeFieldRef.current = secField.field;
              }
            } catch (initErr) {
              Debugger.error('Error al inicializar campos de Mercado Pago:', initErr);
              captureMercadoPagoError(initErr, {
                feature: 'CardEditForm',
                step: 'initFields',
              });
            }
          })();
        }, 100);
      } catch (initError) {
        Debugger.error('Error al inicializar campos de Mercado Pago:', initError);
        captureMercadoPagoError(initError, {
          feature: 'CardEditForm',
          step: 'initFields',
        });
      }
    };

    initFields();

    // eslint-disable-next-line consistent-return
    return () => {
      // Usar el util helper para limpiar los campos
      cleanupEditCardFields().catch(() => {
        // Ignorar errores al limpiar
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateForm = () => {
    const errs = editFormLiterals.errors || {};
    const newErrors = {};

    if (!cardholderName || cardholderName.trim().length < 3) {
      newErrors.cardholderName = errs.invalidName || 'Nombre del titular inválido';
    }

    if (!paymentMethodId) {
      newErrors.paymentMethod = errs.invalidPaymentMethod || 'Por favor, ingresa un número de tarjeta válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!mpInstanceRef.current) {
      setError(editFormLiterals.errors?.notReady || 'Mercado Pago no está listo. Por favor, intenta de nuevo.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await mpInstanceRef.current.fields.createCardToken({
        cardholderName,
        identificationType: 'DNI',
        identificationNumber: '',
      });

      if (!token || !token.id) {
        throw new Error(editFormLiterals.errors?.tokenFailed || 'No se pudo generar el token.');
      }

      // Validar que los primeros 6 y últimos 4 dígitos coincidan con la tarjeta original
      const originalFirstSix = card?.first_six_digits || '';
      const originalLastFour = card?.last_four_digits || '';
      const tokenFirstSix = token.first_six_digits || '';
      const tokenLastFour = token.last_four_digits || '';

      if (originalFirstSix && originalLastFour) {
        if (tokenFirstSix !== originalFirstSix || tokenLastFour !== originalLastFour) {
          setLoading(false);
          setError(editFormLiterals.errors?.differentCard || 'La tarjeta ingresada no coincide con la tarjeta que estás editando. Por favor, verifica que los primeros 6 y últimos 4 dígitos sean correctos.');
          return;
        }
      }

      onUpdate({
        token: token.id || '',
        paymentMethodId,
        cardholderName,
        issuer: issuer || '',
        cardNumber: token.trunc_card_number.replace(/X/g, '*') || '',
        cardType: paymentTypeIdToCardType(paymentTypeId),
        last_four_digits: token.last_four_digits || card?.last_four_digits || '',
      });

      setLoading(false);
    } catch (tokenError) {
      captureMercadoPagoError(tokenError, { feature: 'CardEditForm', step: 'tokenization' });
      setError(editFormLiterals.errors?.processFailed || 'Error al procesar la tarjeta');
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (card && onDelete) {
      onDelete(card);
    }
  };

  if (!card) {
    return null;
  }

  return (
    <>
      <form id='edit-card-form-checkout' onSubmit={handleSubmit}>
        <div className='card-edit-form'>
          <h4 className='mb-4'>{editFormLiterals.title || 'Editar tarjeta'}</h4>

          {/* Mostrar información de la tarjeta actual */}
          <div className='mb-4 p-4 bg-light rounded'>
            <p className='mb-1 text-neutral-500 small'>{editFormLiterals.editingLabel || 'Editando tarjeta'}</p>
            <div className='flex items-center gap-2'>
              {card.payment_method?.thumbnail && (
                <img
                  src={card.payment_method.thumbnail}
                  alt={card.payment_method?.name || 'Tarjeta'}
                  className='card-brand-logo-small'
                  style={{ width: '40px', height: 'auto' }}
                />
              )}
              <div>
                <p className='mb-0 font-bold'>{card.payment_method?.name || 'Tarjeta'}</p>
                <p className='mb-0 text-neutral-500 small'>
                  ••••
                  {' '}
                  {card.last_four_digits}
                </p>
              </div>
            </div>
          </div>

          <div className='mb-4'>
            <label className='form-label'>{editFormLiterals.cardNumber || 'Número de tarjeta'}</label>
            <div id='edit-card-form__cardNumber' className='mp-field-container' />
          </div>

          <div className='grid grid-cols-2 gap-x-6 mb-4'>
            <div>
              <label className='form-label'>{editFormLiterals.expirationDate || 'Fecha de expiración'}</label>
              <div id='edit-card-form__expirationDate' className='mp-field-container' />
            </div>
            <div>
              <label className='form-label'>{editFormLiterals.securityCode || 'Código de seguridad'}</label>
              <div id='edit-card-form__securityCode' className='mp-field-container' />
            </div>
          </div>

          <Input
            label={editFormLiterals.cardholder || 'Titular de la tarjeta'}
            value={cardholderName}
            onChange={setCardholderName}
            placeholder={editFormLiterals.cardholderPlaceholder || 'Titular de la tarjeta'}
            error={errors.cardholderName}
            className='mb-4'
          />

          {issuerThumbnail && (
            <div className='mb-4'>
              <label className='form-label'>{editFormLiterals.issuer || 'Banco emisor'}</label>
              <div className='payment-form-issuer-box'>
                <img
                  src={issuerThumbnail}
                  alt={issuerName || editFormLiterals.issuer}
                  className='payment-form-issuer-img'
                />
                <span className='payment-form-issuer-name'>{issuerName || editFormLiterals.issuer}</span>
              </div>
            </div>
          )}

          {errors.paymentMethod && (
            <div className='alert alert-danger mb-4'>{errors.paymentMethod}</div>
          )}

          <div className='flex flex-col-reverse gap-2 mb-4'>
            <Button
              text={editFormLiterals.cancel || literals.common?.cancel || 'Cancelar'}
              type='secondary'
              className='flex-1'
              onClick={onCancel}
              disabled={loading}
            />
            <Button
              text={editFormLiterals.saveButton || 'Guardar cambios'}
              type='primary'
              className='flex-1'
              onClick={handleSubmit}
              loading={loading}
              disabled={loading}
            />
          </div>

          {/* Botón de eliminar separado visualmente */}
          <div className='mt-6 pt-4 border-t'>
            <Button
              text={editFormLiterals.deleteButton || 'Eliminar tarjeta'}
              type='secondary'
              className='w-full'
              onClick={handleDelete}
              disabled={loading}
              style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
            />
          </div>
        </div>
      </form>

      {error && (
        <ErrorModal
          title={literals.errors?.title || 'Error'}
          description={error}
          onClose={() => setError(null)}
        />
      )}
    </>
  );
};

CardEditForm.propTypes = {
  card: PropTypes.object,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default CardEditForm;
